import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  Chip,
  Alert,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SendIcon from "@mui/icons-material/Send";
import { submit_custom_land_enquiry, get_my_custom_land_enquiries } from "../../../api/ControllApi";

export default function CustomLandEnquiry() {
  const [activeTab, setActiveTab] = useState(0); // 0 = Submit, 1 = List
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Form Fields
  const [formData, setFormData] = useState({
    customer_name: "",
    mobile_number: "",
    email_address: "",
    type_of_land: "",
    required_sqft: "",
    purpose: "",
    preferred_location: "",
    budget_range: "",
    additional_requirements: "",
    contact_through: "Phone Call",
  });

  const [formErrors, setFormErrors] = useState({});

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const res = await get_my_custom_land_enquiries();
      if (res && res.data) {
        setEnquiries(res.data);
      }
    } catch (err) {
      console.error("Error fetching enquiries:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchEnquiries();
    }
  }, [activeTab]);

  const validateForm = () => {
    const errors = {};
    if (!formData.customer_name.trim()) errors.customer_name = "Customer name is mandatory.";
    
    if (!formData.mobile_number.trim()) {
      errors.mobile_number = "Mobile number is mandatory.";
    } else if (!/^[0-9]{10}$/.test(formData.mobile_number.trim())) {
      errors.mobile_number = "Mobile number must contain exactly 10 digits.";
    }

    if (formData.email_address.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address.trim())) {
      errors.email_address = "Please enter a valid email address.";
    }

    if (!formData.type_of_land.trim()) errors.type_of_land = "Type of land is mandatory.";
    
    if (!formData.required_sqft.toString().trim()) {
      errors.required_sqft = "Required Sq.ft. size is mandatory.";
    } else {
      const sqft = parseFloat(formData.required_sqft);
      if (isNaN(sqft) || sqft <= 0) {
        errors.required_sqft = "Sq.ft. size must be a number greater than zero.";
      }
    }

    if (!formData.purpose.trim()) errors.purpose = "Purpose of land purchase is mandatory.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setErrorMsg(null);

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const res = await submit_custom_land_enquiry(formData);
      if (res && res.status === 200) {
        setMessage(res.message || "Your enquiry has been submitted successfully. Our team will contact you shortly.");
        // Reset form
        setFormData({
          customer_name: "",
          mobile_number: "",
          email_address: "",
          type_of_land: "",
          required_sqft: "",
          purpose: "",
          preferred_location: "",
          budget_range: "",
          additional_requirements: "",
        });
        setFormErrors({});
      } else {
        setErrorMsg(res.message || "Something went wrong. Please check your inputs.");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.message || "Failed to submit enquiry. Please verify network connection.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    const s = status ? status.toLowerCase() : "new";
    switch (s) {
      case "new":
        return <Chip label="New" size="small" sx={{ bgcolor: "#fff3cd", color: "#856404", fontWeight: 600 }} />;
      case "in progress":
        return <Chip label="In Progress" size="small" sx={{ bgcolor: "#d1ecf1", color: "#0c5460", fontWeight: 600 }} />;
      case "contacted":
        return <Chip label="Contacted" size="small" sx={{ bgcolor: "#cce5ff", color: "#004085", fontWeight: 600 }} />;
      case "site visit scheduled":
        return <Chip label="Site Visit Scheduled" size="small" sx={{ bgcolor: "#d4edda", color: "#155724", fontWeight: 600 }} />;
      case "quotation sent":
        return <Chip label="Quotation Sent" size="small" sx={{ bgcolor: "#d4edda", color: "#155724", fontWeight: 600 }} />;
      case "closed":
        return <Chip label="Closed" size="small" sx={{ bgcolor: "#e2e3e5", color: "#383d41", fontWeight: 600 }} />;
      case "cancelled":
        return <Chip label="Cancelled" size="small" sx={{ bgcolor: "#f8d7da", color: "#721c24", fontWeight: 600 }} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 4 }, bgcolor: "#fafafa", minHeight: "85vh" }}>
      {/* Header Panel */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #82A240 0%, #6A8A35 100%)",
          color: "#fff",
          p: 4,
          borderRadius: 4,
          mb: 4,
          boxShadow: "0 8px 24px rgba(130, 162, 64, 0.2)",
        }}
      >
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Submit Custom Land Requirements
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Looking for specific land? Submit your customized requirements below, and our team will get back to you with matches.
        </Typography>
      </Paper>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            "& .MuiTab-root": { fontWeight: 700, textTransform: "none", fontSize: "1.05rem" },
            "& .Mui-selected": { color: "#82A240 !important" },
            "& .MuiTabs-indicator": { bgcolor: "#82A240" },
          }}
        >
          <Tab label="Submit Land Enquiry" />
          <Tab label="My Enquiries List" />
        </Tabs>
      </Box>

      {/* Tab Content 1: Submit Form */}
      {activeTab === 0 && (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: "1px solid #eef" }}>
          {message && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>
              {message}
            </Alert>
          )}
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setErrorMsg(null)}>
              {errorMsg}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Customer Information */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#444" }}>
              Customer Information
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Customer Name *"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  error={!!formErrors.customer_name}
                  helperText={formErrors.customer_name}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Mobile Number (10 digits) *"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  error={!!formErrors.mobile_number}
                  helperText={formErrors.mobile_number}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email Address (Optional)"
                  name="email_address"
                  type="email"
                  value={formData.email_address}
                  onChange={handleInputChange}
                  error={!!formErrors.email_address}
                  helperText={formErrors.email_address}
                />
              </Grid>
            </Grid>

            {/* Land Requirement details */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#444" }}>
              Land Requirement Details
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="What type of land are you looking for? *"
                  name="type_of_land"
                  value={formData.type_of_land}
                  onChange={handleInputChange}
                  placeholder="e.g. Residential Plot, Commercial Land, Agricultural Land..."
                  error={!!formErrors.type_of_land}
                  helperText={formErrors.type_of_land}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="How many Sq.ft. do you require? *"
                  name="required_sqft"
                  type="number"
                  value={formData.required_sqft}
                  onChange={handleInputChange}
                  placeholder="Enter required land size in Sq.ft."
                  error={!!formErrors.required_sqft}
                  helperText={formErrors.required_sqft}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="For what purpose do you need this land? *"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  placeholder="e.g. House Construction, Investment, Farming, Warehouse..."
                  error={!!formErrors.purpose}
                  helperText={formErrors.purpose}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Preferred Location (Optional)"
                  name="preferred_location"
                  value={formData.preferred_location}
                  onChange={handleInputChange}
                  placeholder="Enter preferred city, locality, area, or district."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Budget Range (Optional)"
                  name="budget_range"
                  value={formData.budget_range}
                  onChange={handleInputChange}
                  placeholder="Example: ₹20 Lakhs - ₹30 Lakhs"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  label="Contact Through (Preferred Contact Method)"
                  name="contact_through"
                  value={formData.contact_through || "Phone Call"}
                  onChange={handleInputChange}
                >
                  <option value="Phone Call">Phone Call</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Email">Email</option>
                  <option value="WhatsApp / Call">WhatsApp / Call</option>
                </TextField>
              </Grid>
            </Grid>

            {/* Multi-line additional requirements */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Additional Requirements (Optional)"
                  name="additional_requirements"
                  value={formData.additional_requirements}
                  onChange={handleInputChange}
                  placeholder="Mention corner plot, east-facing, DTCP/CMDA approved, highway, water, road, gated community, etc."
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                disabled={submitting}
                sx={{
                  bgcolor: "#82A240",
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: "20px",
                  "&:hover": { bgcolor: "#718F36" },
                }}
              >
                {submitting ? "Submitting..." : "Submit Enquiry"}
              </Button>
            </Box>
          </form>
        </Paper>
      )}

      {/* Tab Content 2: List View */}
      {activeTab === 1 && (
        <TableContainer component={Paper} sx={{ borderRadius: 3, border: "1px solid #eef", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#82A240" }} />
            </Box>
          ) : enquiries.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <AssignmentIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Enquiries Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You haven't submitted any custom land requirement enquiries yet.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Enquiry ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Submitted Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type of Land</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Required Size</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Preferred Location</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Budget Range</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enquiries.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell fontWeight={700} color="#82A240">{item.enquiry_id}</TableCell>
                    <TableCell>{new Date(item.created_at).toLocaleString()}</TableCell>
                    <TableCell><Chip label={item.type_of_land} size="small" variant="outlined" /></TableCell>
                    <TableCell>{Number(item.required_sqft).toLocaleString()} Sq.ft.</TableCell>
                    <TableCell>{item.purpose}</TableCell>
                    <TableCell>{item.preferred_location || "-"}</TableCell>
                    <TableCell>{item.budget_range || "-"}</TableCell>
                    <TableCell>{getStatusChip(item.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}
    </Box>
  );
}
